import { normalizeSurfFont } from "../style/surfFonts.js";
import { resolveRoleColor } from "../style/surfStyle.js";
import { fitTextSizeRel } from "./fitText.js";
export const TEXT_OVERRIDE_KEY = "text";
export function getTextOverride(layer) {
    const raw = layer.style?.[TEXT_OVERRIDE_KEY];
    if (!raw || typeof raw !== "object")
        return {};
    return raw;
}
export function setTextOverride(layer, patch) {
    const prev = getTextOverride(layer);
    const next = { ...prev, ...patch };
    const cleaned = Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined && v !== ""));
    return {
        ...layer,
        style: {
            ...layer.style,
            [TEXT_OVERRIDE_KEY]: Object.keys(cleaned).length ? cleaned : undefined,
        },
    };
}
export function clearTextOverride(layer) {
    const { [TEXT_OVERRIDE_KEY]: _removed, ...rest } = layer.style ?? {};
    return { ...layer, style: rest };
}
function resolveOverrideColor(theme, role, overrideColor) {
    if (!overrideColor || overrideColor === "role") {
        return resolveRoleColor(theme, role);
    }
    const ref = overrideColor.toLowerCase();
    if (ref === "surface")
        return theme.palette.surface;
    if (ref === "text")
        return theme.palette.text;
    if (ref === "accent")
        return theme.palette.accent;
    if (ref === "muted")
        return theme.palette.muted;
    if (/^#?[0-9a-f]{6}$/i.test(overrideColor.trim())) {
        const hex = overrideColor.trim();
        return hex.startsWith("#") ? hex : `#${hex}`;
    }
    return resolveRoleColor(theme, role);
}
/** Single resolver for canvas, PDF, and PPTX text styling. */
export function resolveTextStyle(layer, theme) {
    const role = layer.textRole ?? "body";
    const tr = theme.textRoles[role];
    const ov = getTextOverride(layer);
    const slotRole = layer.style?.slotRole;
    let weight = ov.weight ?? tr.weight;
    let sizeRel = ov.sizeRel ?? tr.sizeRel;
    let align = ov.align ?? tr.align;
    let lineHeight = ov.lineHeight ?? tr.lineHeight;
    if (slotRole === "number") {
        weight = ov.weight ?? 800;
        sizeRel = ov.sizeRel ?? Math.max(tr.sizeRel, 0.14);
        align = ov.align ?? "center";
        lineHeight = ov.lineHeight ?? 1.05;
    }
    else if (slotRole === "quote") {
        sizeRel = ov.sizeRel ?? Math.max(tr.sizeRel, 0.056);
        align = ov.align ?? "center";
        lineHeight = ov.lineHeight ?? 1.35;
    }
    else if (slotRole === "label" && (layer.textRole === "subtitle" || slotRole === "label")) {
        sizeRel = ov.sizeRel ?? Math.max(tr.sizeRel, 0.048);
        align = ov.align ?? "center";
    }
    return {
        font: normalizeSurfFont(ov.font ?? tr.font),
        weight,
        sizeRel,
        color: ov.color !== undefined
            ? resolveOverrideColor(theme, role, ov.color)
            : resolveRoleColor(theme, role),
        align,
        lineHeight,
        tracking: ov.tracking ?? tr.tracking,
        case: ov.case ?? tr.case,
        maxLines: tr.maxLines,
    };
}
export function applyTextCase(text, textCase) {
    return textCase === "upper" ? text.toUpperCase() : text;
}
/** Resolve text style with fit-to-box shrink when content overflows layer rect. */
export function resolveFittedTextStyle(layer, theme, layout) {
    const ts = resolveTextStyle(layer, theme);
    if (layer.type !== "text")
        return ts;
    const text = applyTextCase(layer.content?.trim() || "", ts.case);
    if (!text)
        return ts;
    const fitted = fitTextSizeRel(text, { x: layer.x, y: layer.y, w: layer.w, h: layer.h }, ts.sizeRel, ts.lineHeight, ts.maxLines, layout, ts.font, ts.weight);
    if (fitted >= ts.sizeRel - 0.0001)
        return ts;
    return { ...ts, sizeRel: fitted };
}
